import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Package, Truck, ArrowRight } from 'lucide-react';
import { ProxyImage } from '../../lib/imageProxy';
import AddToCartButton from '../AddToCartButton';
import { PriceDisplay, DiscountBadge } from '../ui/PriceDisplay';
import { ProductCardSkeleton } from '../ui/Skeleton';
import { useAuth } from '../../contexts/AuthContext';

export type ProductCardVariant = 'default' | 'featured' | 'compact' | 'list' | 'minimal' | 'catalog';

export interface ProductCardProduct {
  id: string;
  name: string;
  description?: string;
  main_image: string;
  stock?: number;
  min_order?: number;
  code?: string;
  category?: {
    name: string;
    slug: string;
  };
}

export interface ProductCardPriceInfo {
  price: number;
  originalPrice?: number | null;
  isOnSale?: boolean;
  discountPercentage?: number | null;
  offerDays?: number | null;
}

interface ProductCardProps {
  product: ProductCardProduct;
  priceInfo?: ProductCardPriceInfo;
  variant?: ProductCardVariant;
  showCategory?: boolean;
  showCode?: boolean;
  showMinOrder?: boolean;
  showFreeShipping?: boolean;
  showAddToCart?: boolean;
  showQuickBuy?: boolean;
  loading?: boolean;
  className?: string;
  onHover?: (productId: string | null) => void;
}

/**
 * Componente unificado de tarjeta de producto.
 * Reemplaza las múltiples implementaciones inconsistentes.
 *
 * @example
 * // Tarjeta default
 * <ProductCard product={product} priceInfo={priceInfo} />
 *
 * // Tarjeta featured (con hover overlay)
 * <ProductCard product={product} priceInfo={priceInfo} variant="featured" />
 *
 * // Tarjeta compact (grid pequeño)
 * <ProductCard product={product} priceInfo={priceInfo} variant="compact" />
 */
export const ProductCard = memo(function ProductCard({
  product,
  priceInfo,
  variant = 'default',
  showCategory = true,
  showCode = false,
  showMinOrder = true,
  showFreeShipping = false,
  showAddToCart = true,
  showQuickBuy = false,
  loading = false,
  className = '',
  onHover
}: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { user } = useAuth();

  if (loading) {
    return <ProductCardSkeleton />;
  }

  const handleMouseEnter = () => {
    setIsHovered(true);
    onHover?.(product.id);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    onHover?.(null);
  };

  const price = priceInfo?.price ?? 0;
  const originalPrice = priceInfo?.originalPrice;
  const isOnSale = priceInfo?.isOnSale ?? false;
  const discountPercentage = priceInfo?.discountPercentage;
  const offerDays = priceInfo?.offerDays;
  const minOrder = product.min_order ?? 1;

  // Variante DEFAULT (original ProductCard)
  if (variant === 'default') {
    return (
      <div className={`bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group ${className}`}>
        <Link to={`/product/${product.id}`} className="block">
          <div className="relative">
            <ProxyImage
              src={product.main_image}
              alt={product.name}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {showMinOrder && minOrder > 1 && (
              <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                <Package className="w-3 h-3" />
                Lote de {minOrder}
              </div>
            )}
          </div>
        </Link>
        <div className="p-4">
          {showCategory && product.category && (
            <div className="text-xs text-gray-500 mb-1">
              {product.category.name}
            </div>
          )}
          <Link to={`/product/${product.id}`}>
            <h3 className="text-sm text-gray-900 font-medium line-clamp-2 group-hover:text-blue-600 transition-colors">
              {product.name}
            </h3>
          </Link>
          {showCode && product.code && (
            <p className="text-xs text-gray-500 mt-1">
              Código: {product.code}
            </p>
          )}
          <div className="mt-2 flex items-center justify-between">
            <div className="space-y-1">
              <PriceDisplay
                price={price}
                originalPrice={originalPrice}
                isOnSale={isOnSale}
                variant="default"
              />
              {showMinOrder && minOrder > 1 && (
                <p className="text-xs text-gray-500">
                  desde {minOrder} pzas.
                </p>
              )}
            </div>
          </div>
          {showAddToCart && (
            <AddToCartButton
              productId={product.id}
              quantity={minOrder}
              className="w-full mt-4"
            />
          )}
        </div>
      </div>
    );
  }

  // Variante FEATURED (FeaturedProducts style)
  if (variant === 'featured') {
    return (
      <div
        className={`product-card group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showFreeShipping && (
          <div className="absolute top-3 right-3 z-10 bg-green-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-full flex items-center gap-1">
            <Truck className="w-3 h-3" />
            Envío Gratis
          </div>
        )}

        <Link to={`/product/${product.id}`} className="block relative overflow-hidden">
          <div className="aspect-[4/3] bg-white">
            <ProxyImage
              src={product.main_image}
              alt={product.name}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              <ShoppingCart className="w-4 h-4" />
              Añadir al carrito
            </button>
          </div>
        </Link>

        <div className="product-card-content p-4 space-y-3">
          <Link to={`/product/${product.id}`} className="block">
            <h3 className="text-sm text-gray-900 font-medium line-clamp-2 group-hover:text-blue-600 transition-colors">
              {product.name}
            </h3>
            {showCode && product.code && (
              <p className="text-xs text-gray-500 mt-1">
                Código: {product.code}
              </p>
            )}
          </Link>

          <PriceDisplay
            price={price}
            originalPrice={originalPrice}
            isOnSale={isOnSale}
            offerDays={offerDays}
            variant="featured"
          />

          {showQuickBuy && (
            <div className="grid grid-cols-2 gap-2 mt-auto">
              <button className="bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                Añadir
              </button>
              <Link
                to={`/product/${product.id}`}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                Ver detalles
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Variante COMPACT (NewProducts style)
  if (variant === 'compact') {
    return (
      <div
        className={`group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isOnSale && discountPercentage && (
          <DiscountBadge percentage={discountPercentage} variant="corner" />
        )}

        <Link to={`/product/${product.id}`} className="block relative">
          <div className="aspect-square bg-gray-50">
            <ProxyImage
              src={product.main_image}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        </Link>

        <div className="p-3 space-y-2">
          <Link to={`/product/${product.id}`} className="block">
            <h3 className="text-sm text-gray-900 font-medium line-clamp-2 group-hover:text-blue-600 transition-colors">
              {product.name}
            </h3>
            {showCode && product.code && (
              <p className="text-xs text-gray-500 mt-1">
                Código: {product.code}
              </p>
            )}
          </Link>

          <PriceDisplay
            price={price}
            originalPrice={originalPrice}
            isOnSale={isOnSale}
            variant="compact"
          />
        </div>

        {/* Quick buy overlay on hover */}
        <div
          className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-blue-600 to-blue-600/90 text-white text-xs font-medium py-2 text-center transform transition-transform duration-300 ${
            isHovered ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          Comprar ahora
        </div>
      </div>
    );
  }

  // Variante MINIMAL (RelatedProducts style)
  if (variant === 'minimal') {
    return (
      <Link
        to={`/product/${product.id}`}
        className={`group block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}
      >
        <div className="aspect-square w-full overflow-hidden rounded-t-lg bg-gray-100">
          <ProxyImage
            src={product.main_image}
            alt={product.name}
            className="h-full w-full object-cover object-center group-hover:opacity-75 transition-opacity"
          />
        </div>
        <div className="p-3">
          <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
            {product.name}
          </h3>
          {showCode && product.code && (
            <p className="text-xs text-gray-500 mt-1">
              Código: {product.code}
            </p>
          )}
          <div className="mt-2 flex items-center justify-between">
            <PriceDisplay
              price={price}
              originalPrice={originalPrice}
              isOnSale={isOnSale}
              variant="inline"
            />
            {isOnSale && (
              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                Oferta
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  // Variante LIST
  if (variant === 'list') {
    return (
      <div className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 flex gap-4 p-4 ${className}`}>
        <Link to={`/product/${product.id}`} className="flex-shrink-0">
          <ProxyImage
            src={product.main_image}
            alt={product.name}
            className="w-32 h-32 object-cover rounded-lg"
          />
        </Link>
        <div className="flex-1 min-w-0">
          {showCategory && product.category && (
            <div className="text-xs text-gray-500 mb-1">
              {product.category.name}
            </div>
          )}
          <Link to={`/product/${product.id}`}>
            <h3 className="text-base font-medium text-gray-900 hover:text-blue-600 transition-colors line-clamp-2">
              {product.name}
            </h3>
          </Link>
          {showCode && product.code && (
            <p className="text-xs text-gray-500 mt-1">
              Código: {product.code}
            </p>
          )}
          {product.description && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
              {product.description}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between">
            <PriceDisplay
              price={price}
              originalPrice={originalPrice}
              isOnSale={isOnSale}
              discountPercentage={discountPercentage}
              variant="default"
            />
            {showAddToCart && (
              <AddToCartButton
                productId={product.id}
                quantity={minOrder}
                className="flex-shrink-0"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Variante CATALOG (para páginas de catálogo con autenticación)
  if (variant === 'catalog') {
    const isOutOfStock = (product.stock ?? 0) <= 0;

    return (
      <Link
        to={`/product/${product.id}`}
        className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col ${className}`}
      >
        <div className="relative bg-gray-50 overflow-hidden aspect-square">
          <ProxyImage
            src={product.main_image}
            alt={product.name}
            className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-300"
          />
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white font-semibold bg-black/50 px-4 py-2 rounded-full">Agotado</span>
            </div>
          )}
          {isOnSale && discountPercentage && (
            <DiscountBadge percentage={discountPercentage} variant="corner" />
          )}
        </div>
        <div className="p-4 flex-1 flex flex-col">
          {showCategory && product.category && (
            <div className="text-xs text-blue-600 font-semibold mb-2">
              {product.category.name}
            </div>
          )}
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors mb-2 flex-1">
            {product.name}
          </h3>
          {showCode && product.code && (
            <p className="text-xs text-gray-500 mb-3">
              Código: {product.code}
            </p>
          )}
          <div className="mt-auto">
            <div className="mb-3">
              <PriceDisplay
                price={price}
                originalPrice={originalPrice}
                isOnSale={isOnSale}
                variant="default"
              />
            </div>
            {showAddToCart && (
              user ? (
                <AddToCartButton
                  productId={product.id}
                  quantity={minOrder}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg"
                />
              ) : (
                <Link
                  to="/login"
                  onClick={(e) => e.stopPropagation()}
                  className="w-full bg-gray-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  Iniciar sesión
                </Link>
              )
            )}
          </div>
        </div>
      </Link>
    );
  }

  // Fallback
  return null;
});

/**
 * Componente para mostrar enlace a "Ver más" en sliders
 */
interface ViewMoreCardProps {
  href?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

export const ViewMoreCard = memo(function ViewMoreCard({
  href = '/catalog',
  title = 'Ver catálogo completo',
  subtitle = 'Explora todos nuestros productos',
  className = ''
}: ViewMoreCardProps) {
  return (
    <div className={`product-card flex flex-col items-center justify-center h-full bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group ${className}`}>
      <Link
        to={href}
        className="flex flex-col items-center justify-center p-8 h-full w-full"
      >
        <ArrowRight className="w-12 h-12 text-gray-400 group-hover:text-blue-500 mb-4" />
        <span className="text-lg font-medium text-gray-600 group-hover:text-blue-600">
          {title}
        </span>
        <span className="text-sm text-gray-500">{subtitle}</span>
      </Link>
    </div>
  );
});

export default ProductCard;
